import { API } from '@/configs'
import { useLazyRestQuery } from '@/domains/appShell/services/useLazyRestQuery'
import { isMaintenanceModeAtom } from '@/domains/appShell/stores/maintenance.store'

import { useEffect } from 'react'

import { useAtom } from 'jotai'

/** Delay between two recovery checks while the maintenance modal is open. */
export const MAINTENANCE_POLL_INTERVAL_MS = 10_000

/**
 * Controller hook for the blocking maintenance modal.
 *
 * Reads/writes the global `isMaintenanceModeAtom` (set to `true` by the central Axios response
 * interceptor as soon as any REST call receives a 503). While the modal is open, it polls a
 * lightweight already-authenticated endpoint (`API.user.info`) on a fixed interval; as soon as
 * that call succeeds, the modal closes itself automatically. Failures are ignored silently — the
 * next tick will simply retry.
 */
export const useMaintenanceModalController = () => {
  const [isOpen, setIsOpen] = useAtom(isMaintenanceModeAtom)

  const { fetch: checkRecovery } = useLazyRestQuery(['maintenance-check'], API.user.info)

  useEffect(() => {
    if (!isOpen) return

    const intervalId = setInterval(() => {
      void (async () => {
        const result = await checkRecovery()
        if (!result.isError) {
          setIsOpen(false)
        }
      })()
    }, MAINTENANCE_POLL_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [isOpen, checkRecovery, setIsOpen])

  return { isOpen, setIsOpen }
}
